import { Stack, Typography } from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  ENABLE_ACCOUNT_ROUTE,
  H1,
  H3,
  IS_RECAPTCHA_ENABLED,
  LOGIN_ROUTE,
  MODE_ALL,
  USER_VERIFIED_SUCCESSFULLY,
  VERIFY_ACCOUNT_ROUTE,
} from "../../../Constants/CommonConstants";
import {
  ENABLE_YOUR_ACCOUNT_CONTENT_1,
  ENABLE_YOUR_ACCOUNT_FORM,
  ENABLE_YOUR_ACCOUNT_HEADER,
} from "../../../Constants/EnableYourAccountConstant";
import { MATCH_NOT_FOUND } from "../../../Constants/ForgotUsernameConstants";
import { FLOW_TEXT_ACCOUNT } from "../../../Constants/NoMatchFoundModalConstants";
import {
  SLICE_LOGIN,
  THUNK_POST_IDENTIFY_USER,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearLoginApiResponse,
  postIdentifyUser,
} from "../../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageLoginSelector } from "../../../Selectors/LoginSelector";
import { StyledTypographyHeader } from "../../../Styles/Common/CommonStyles";
import GoogleReCaptcha from "../../Application/GoogleReCaptcha";
import NoMatchFoundModal from "../../Common/NoMatchFoundModal/NoMatchFoundModal";
import EnableYourAccountFormElements from "./EnableYourAccountFormElements";
import { isEmpty } from "lodash-es";
import ConfigService from "../../../Services/ConfigService";

const isReCaptchaEnabled = ConfigService(IS_RECAPTCHA_ENABLED);

/**
 * @description Enable your account parent component.
 * @returns {ReactNode} Renders the heading and the form component.
 */
const EnableYourAccount = () => {
  const intl = useIntl();

  const [noMatchFoundModalOpen, setNoMatchFoundModalOpen] = useState(false);

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_LOGIN, THUNK_POST_IDENTIFY_USER)
  );

  const { userName } = useSelector((state) => manageLoginSelector(state));

  const { isFailure, isSuccess, description } = apiResponses;

  const methods = useForm({
    mode: MODE_ALL,
  });

  const navigate = useNavigate();

  const enableYourAccountRef = useRef();

  const dispatch = useDispatch();

  const handleOnSubmit = async (data) => {
    const token = isReCaptchaEnabled
      ? await enableYourAccountRef.current.onSubmitWithReCAPTCHA()
      : null;
    if (token || !isReCaptchaEnabled) {
      const payload = {
        userName: userName,
        dob: data.dateOfBirth,
        reCaptchaTkn: token,
      };
      dispatch(postIdentifyUser(payload));
    }
  };

  useEffect(() => {
    if (isSuccess && description === USER_VERIFIED_SUCCESSFULLY) {
      navigate(`/${LOGIN_ROUTE}/${VERIFY_ACCOUNT_ROUTE}`, {
        state: {
          previousScreen: ENABLE_ACCOUNT_ROUTE,
        },
      });
      return;
    }
    if (isFailure && description === MATCH_NOT_FOUND) {
      setNoMatchFoundModalOpen(true);
    }
    dispatch(clearLoginApiResponse(THUNK_POST_IDENTIFY_USER));
  }, [description, dispatch, isFailure, isSuccess, navigate]);

  useEffect(() => {
    if (isEmpty(userName)) {
      navigate(`/${LOGIN_ROUTE}`);
    }
  }, [navigate, userName]);

  return (
    <Fragment>
      <Stack spacing={{ xs: 1.75, md: 5 }}>
        <Stack spacing={{ xs: 1.75, md: 2 }}>
          <StyledTypographyHeader
            id={ENABLE_YOUR_ACCOUNT_HEADER}
            component={H1}
            variant={H3}
          >
            {intl.formatMessage({
              id: ENABLE_YOUR_ACCOUNT_HEADER,
            })}
          </StyledTypographyHeader>
          <Typography id={ENABLE_YOUR_ACCOUNT_CONTENT_1}>
            {intl.formatMessage({
              id: ENABLE_YOUR_ACCOUNT_CONTENT_1,
            })}
          </Typography>
        </Stack>
        <Stack spacing={5}>
          <FormProvider {...methods}>
            <form
              id={ENABLE_YOUR_ACCOUNT_FORM}
              onSubmit={methods.handleSubmit(handleOnSubmit)}
            >
              <EnableYourAccountFormElements />
            </form>
          </FormProvider>
        </Stack>
      </Stack>
      {isReCaptchaEnabled ? (
        <GoogleReCaptcha ref={enableYourAccountRef} />
      ) : null}
      <NoMatchFoundModal
        flow={FLOW_TEXT_ACCOUNT}
        open={noMatchFoundModalOpen}
        setOpen={setNoMatchFoundModalOpen}
      />
    </Fragment>
  );
};

export default EnableYourAccount;
